const API_BASE = import.meta.env.VITE_API_URL || '/api';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'USER' | 'ADMIN';
  emailVerified?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  twoFactorEnabled?: boolean;
}

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.message || data.error || 'An error occurred',
      data.details
    );
  }

  return data;
}

export interface LoginResult {
  requiresLoginCode: boolean;
  requires2FA: boolean;
  email?: string;
  role?: 'USER' | 'ADMIN';
}

export const authApi = {
  signup: async (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => {
    return fetchApi<{ message: string; email: string; requiresActivation?: boolean }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  activateAccount: async (email: string, activationToken: string) => {
    return fetchApi<{ message: string }>('/auth/activate', {
      method: 'POST',
      body: JSON.stringify({ email, activationToken }),
    });
  },

  resendActivation: async (email: string) => {
    return fetchApi<{ message: string }>('/auth/resend-activation', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  login: async (email: string, password: string, clientId?: string) => {
    return fetchApi<{
      user?: User;
      requiresLoginCode?: boolean;
      requires2FA?: boolean;
      email?: string;
      message?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(clientId ? { client_id: clientId } : {}) }),
    });
  },

  verifyLoginCode: async (email: string, code: string) => {
    return fetchApi<{
      user?: User;
      requires2FA?: boolean;
      email?: string;
      message?: string;
    }>('/auth/login/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },

  verifyLogin2FA: async (email: string, code: string) => {
    return fetchApi<{ user: User; message: string }>('/auth/login/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },

  resendLoginCode: async (email: string) => {
    return fetchApi<{ message: string }>('/auth/login/resend-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  logout: async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch {
      // Cookie clear is best-effort when offline
    }
  },

  getMe: async () => fetchApi<{ user: User | null }>('/auth/me'),

  updateProfile: async (data: { firstName?: string; lastName?: string }) => {
    return fetchApi<{ user: User }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    return fetchApi('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  getSessions: async () => {
    return fetchApi<{
      sessions: Array<{
        id: string;
        userAgent?: string;
        ipAddress?: string;
        createdAt: string;
        expiresAt: string;
        isCurrent: boolean;
      }>;
    }>('/auth/sessions');
  },

  revokeSession: async (sessionId: string) => {
    return fetchApi(`/auth/sessions/${sessionId}`, { method: 'DELETE' });
  },

  getNotificationPreferences: async () => {
    return fetchApi<{
      preferences: {
        notifyProductUpdates: boolean;
        notifyUsageAlerts: boolean;
        notifyBilling: boolean;
        notifySecurity: boolean;
      };
    }>('/auth/notifications');
  },

  updateNotificationPreferences: async (preferences: {
    notifyProductUpdates?: boolean;
    notifyUsageAlerts?: boolean;
    notifyBilling?: boolean;
    notifySecurity?: boolean;
  }) => {
    return fetchApi('/auth/notifications', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  },

  enable2FA: async () => {
    return fetchApi<{
      message: string;
      secret: string;
      backupCodes: string[];
      qrCode: string;
    }>('/auth/2fa/enable', { method: 'POST' });
  },

  verify2FA: async (code: string) => {
    return fetchApi<{ message: string }>('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  disable2FA: async (password: string) => {
    return fetchApi<{ message: string }>('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  deleteAccount: async (password: string, confirmation: string) => {
    return fetchApi<{ message: string }>('/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmation }),
    });
  },

  forgotPassword: async (email: string) => {
    return fetchApi<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPasswordWithToken: async (token: string, newPassword: string) => {
    return fetchApi<{ message: string }>('/auth/reset-password-with-token', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  },

  federatedAuthorize: async (
    clientId: string,
    state: string,
    codeChallenge: string,
    redirectUri: string
  ) => {
    return fetchApi<{ code: string; state: string }>('/auth/federated/authorize', {
      method: 'POST',
      body: JSON.stringify({
        client_id: clientId,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        redirect_uri: redirectUri,
      }),
    });
  },

  getApplication: async (clientId: string) => {
    return fetchApi<{ clientId: string; name: string; description?: string; staffOnly: boolean }>(
      `/auth/applications/${clientId}`
    );
  },
};

export const adminApi = {
  getApplications: async () => {
    return fetchApi<{
      applications: Array<{
        id: string;
        clientId: string;
        name: string;
        description?: string;
        redirectUris: string[];
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
    }>('/admin/applications');
  },

  createApplication: async (data: {
    name: string;
    description?: string;
    redirectUris: string[];
  }) => {
    return fetchApi<{ application: Record<string, unknown> }>('/admin/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateApplication: async (
    id: string,
    data: {
      name?: string;
      description?: string;
      redirectUris?: string[];
      isActive?: boolean;
    }
  ) => {
    return fetchApi<{ application: Record<string, unknown> }>(`/admin/applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deactivateApplication: async (id: string) => {
    return fetchApi<{ message: string }>(`/admin/applications/${id}`, { method: 'DELETE' });
  },

  getAuditLogs: async (limit = 50) => {
    return fetchApi<{ logs: Array<Record<string, unknown>> }>(
      `/admin/audit-logs?limit=${limit}`
    );
  },
  getSecurityAlerts: async (limit = 50) => {
    return fetchApi<{ alerts: Array<Record<string, unknown>> }>(
      `/admin/security-alerts?limit=${limit}`
    );
  },
};
